import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { toggleTheme } from '../store/slices/themeSlice';
import { setBranding } from '../store/slices/brandingSlice';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { Sun, Moon, Loader2, Upload, X, MessageSquare, Mail, RefreshCw, ShieldAlert } from 'lucide-react';
import { API_CONFIG } from '../config/api.config';
import { prepareCommonHeaders } from '../services/baseApi';
import { usePermissions } from '../hooks/usePermissions';

const initialForm = {
  companyName: '',
  address1: '',
  address2: '',
  city: '',
  postcode: '',
  country: '',
};

const initialBrandingForm = {
  logoUrl: '',
  brandColor: '#4f46e5',
  emailAddress: '',
  phone: '',
  website: '',
  reportHeader: '',
  emailFooter: '',
};

type SettingsTab = 'general' | 'branding' | 'sms-credits' | 'appearance';

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

interface SmsCreditsData {
  totalSmsSent: number;
  totalEmailSent: number;
  smsThisMonth: number;
  emailThisMonth: number;
  smsLastMonth: number;
  emailLastMonth: number;
  byMonth: { month: string; sms: number; email: number }[];
}

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const isDark = useSelector((state: RootState) => state.theme.isDark);
  const { canEditSettings } = usePermissions();

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [form, setForm] = useState(initialForm);
  const [saved, setSaved] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [brandingForm, setBrandingFormState] = useState(initialBrandingForm);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [brandingSaveError, setBrandingSaveError] = useState<string | null>(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [smsCredits, setSmsCredits] = useState<SmsCreditsData | null>(null);
  const [smsCreditsLoading, setSmsCreditsLoading] = useState(false);
  const [smsCreditsError, setSmsCreditsError] = useState<string | null>(null);

  const [retentionMode, setRetentionMode] = useState<'indefinite' | 'limited'>('indefinite');
  const [retentionMonthsInput, setRetentionMonthsInput] = useState('12');
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionSaved, setRetentionSaved] = useState(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);

  const [storageStatus, setStorageStatus] = useState<{
    bytesUsed: number | null;
    capBytes: number | null;
    overCap: boolean;
    lastCheckedAt: string | null;
  } | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);

  useEffect(() => {
    if (!canEditSettings) setActiveTab('appearance');
  }, [canEditSettings]);

  useEffect(() => {
    if (!user?.companyID || !user?.companyName || !canEditSettings) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/company-settings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-company-id': String(user.companyID),
            'x-company-name': user.companyName!,
          },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data && Object.keys(data).length > 0) {
            setForm({
              companyName: data.company_name || '',
              address1: data.address1 || '',
              address2: data.address2 || '',
              city: data.city || '',
              postcode: data.postcode || '',
              country: data.country || '',
            });
            setBrandingFormState({
              logoUrl:      data.logo_url      || '',
              brandColor:   data.brand_color   || '#4f46e5',
              emailAddress: data.email_address || '',
              phone:        data.phone         || '',
              website:      data.website       || '',
              reportHeader: data.report_header || '',
              emailFooter:  data.email_footer  || '',
            });
          }
        }
      } catch (err) {
        console.warn('Could not load company settings:', err);
      } finally {
        setLoadingSettings(false);
      }
    };
    load();
  }, [user?.companyID, user?.companyName, canEditSettings]);

  useEffect(() => {
    if (!user?.companyID || !user?.companyName || !canEditSettings) return;
    const loadRetention = async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/company-settings/retention`, {
          method: 'GET',
          headers: prepareCommonHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.retentionMonths != null) {
            setRetentionMode('limited');
            setRetentionMonthsInput(String(data.retentionMonths));
          } else {
            setRetentionMode('indefinite');
          }
        }
      } catch (err) {
        console.warn('Could not load retention policy:', err);
      } finally {
        setRetentionLoading(false);
      }
    };
    loadRetention();
  }, [user?.companyID, user?.companyName, canEditSettings]);

  useEffect(() => {
    if (!user?.companyID || !user?.companyName || !canEditSettings) return;
    const loadStorageStatus = async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/company-settings/storage`, {
          method: 'GET',
          headers: prepareCommonHeaders(),
          credentials: 'include',
        });
        if (res.ok) setStorageStatus(await res.json());
      } catch (err) {
        console.warn('Could not load storage status:', err);
      } finally {
        setStorageLoading(false);
      }
    };
    loadStorageStatus();
  }, [user?.companyID, user?.companyName, canEditSettings]);

  const handleRetentionSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setRetentionSaved(false);
    setRetentionError(null);

    const retentionMonths = retentionMode === 'indefinite' ? null : parseInt(retentionMonthsInput, 10);
    if (retentionMode === 'limited' && (!Number.isFinite(retentionMonths) || (retentionMonths as number) < 1)) {
      setRetentionError('Enter a retention period of at least 1 month');
      return;
    }

    setRetentionSaving(true);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/company-settings/retention`, {
        method: 'PATCH',
        headers: prepareCommonHeaders(),
        credentials: 'include',
        body: JSON.stringify({ retentionMonths }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Save failed (${res.status})`);
      }
      await logAuditEvent(
        AUDIT_ACTIONS.SETTINGS_UPDATE,
        retentionMonths
          ? `Data retention policy set to ${retentionMonths} month(s)`
          : 'Data retention policy set to keep records indefinitely',
        user
      );
      setRetentionSaved(true);
    } catch (err) {
      setRetentionError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSaved(false);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setBrandingSaveError('Logo must be under 2 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setBrandingFormState(prev => ({ ...prev, logoUrl: dataUrl }));
      setBrandingSaved(false);
    };
    reader.readAsDataURL(file);
  };

  const handleBrandingSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setBrandingSaved(false);
    setBrandingSaveError(null);
    setBrandingSaving(true);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/company-settings`, {
        method: 'PATCH',
        headers: prepareCommonHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          company_name: form.companyName,
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          postcode: form.postcode,
          country: form.country,
          logo_url:      brandingForm.logoUrl,
          brand_color:   brandingForm.brandColor,
          email_address: brandingForm.emailAddress,
          phone:         brandingForm.phone,
          website:       brandingForm.website,
          report_header: brandingForm.reportHeader,
          email_footer:  brandingForm.emailFooter,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Save failed (${res.status})`);
      }
      // Apply brand colour immediately without waiting for a re-render cycle
      document.documentElement.style.setProperty('--brand-color', brandingForm.brandColor || '#4f46e5');
      // Update Redux store so header/footer reflect new branding immediately
      dispatch(setBranding({
        companyName:  form.companyName,
        logoUrl:      brandingForm.logoUrl,
        brandColor:   brandingForm.brandColor,
        emailAddress: brandingForm.emailAddress,
        phone:        brandingForm.phone,
        website:      brandingForm.website,
        reportHeader: brandingForm.reportHeader,
        emailFooter:  brandingForm.emailFooter,
      }));
      await logAuditEvent(AUDIT_ACTIONS.SETTINGS_UPDATE, 'Branding settings updated', user);
      setBrandingSaved(true);
    } catch (err) {
      setBrandingSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(false);
    setSaveError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/company-settings`, {
        method: 'PATCH',
        headers: prepareCommonHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          company_name: form.companyName,
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          postcode: form.postcode,
          country: form.country,
          logo_url:      brandingForm.logoUrl,
          brand_color:   brandingForm.brandColor,
          email_address: brandingForm.emailAddress,
          phone:         brandingForm.phone,
          website:       brandingForm.website,
          report_header: brandingForm.reportHeader,
          email_footer:  brandingForm.emailFooter,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Save failed (${res.status})`);
      }
      // Sync Redux so the header/footer pick up the new company name without a hard refresh
      dispatch(setBranding({
        companyName:  form.companyName,
        logoUrl:      brandingForm.logoUrl,
        brandColor:   brandingForm.brandColor,
        emailAddress: brandingForm.emailAddress,
        phone:        brandingForm.phone,
        website:      brandingForm.website,
        reportHeader: brandingForm.reportHeader,
        emailFooter:  brandingForm.emailFooter,
      }));
      await logAuditEvent(
        AUDIT_ACTIONS.SETTINGS_UPDATE,
        `Address settings updated - Company: ${form.companyName}, Address: ${form.address1}, ${form.city}, ${form.postcode}`,
        user
      );
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };


  const inputCls = "mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset";
  const labelCls = "block text-sm font-medium text-gray-700 dark:text-gray-300";

  const tabs: { id: SettingsTab; label: string }[] = useMemo(
    () =>
      canEditSettings
        ? [
            { id: 'general', label: 'General' },
            { id: 'branding', label: 'Branding' },
            { id: 'sms-credits', label: 'SMS Credits' },
            { id: 'appearance', label: 'Appearance' },
          ]
        : [{ id: 'appearance', label: 'Appearance' }],
    [canEditSettings],
  );
  const loadSmsCredits = async () => {
    if (!user?.companyID || !user?.companyName) return;
    setSmsCreditsLoading(true);
    setSmsCreditsError(null);
    try {
      const res = await fetch(`${API_CONFIG.baseURL}/sms-credits`, {
        method: 'GET',
        headers: prepareCommonHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Failed to load (${res.status})`);
      }
      const data: SmsCreditsData = await res.json();
      setSmsCredits(data);
    } catch (err) {
      setSmsCreditsError(err instanceof Error ? err.message : 'Failed to load SMS credits');
    } finally {
      setSmsCreditsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sms-credits' && !smsCredits && !smsCreditsLoading) {
      loadSmsCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.companyID, user?.companyName]);

  const formatMonthLabel = (ym: string) => {
    const [yyyy, mm] = ym.split('-');
    const d = new Date(Number(yyyy), Number(mm) - 1, 1);
    return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Settings</h1>
      {!canEditSettings && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          You can switch light or dark theme here. Company profile, branding, and billing usage can only be changed by an administrator.
        </p>
      )}

      {/* Tab bar */}
      {tabs.length > 1 && (
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {/* ── General tab ── */}
      {activeTab === 'general' && (
        <>
          {/* Site identity — read-only, comes from the database */}
          <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Site Identity</h2>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Login Company Name</label>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">This is the name entered on the login screen — it identifies your account in the database and cannot be changed here.</p>
                <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                  <span className="text-sm text-gray-900 dark:text-white font-medium">{user?.companyName || '—'}</span>
                  <span className="ml-auto text-xs text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded">read-only</span>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Location & Address Details</h2>
          {loadingSettings && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4 bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div>
              <label className={labelCls}>Company Name</label>
              <input type="text" name="companyName" value={form.companyName} onChange={handleChange} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address Line 1</label>
              <input type="text" name="address1" value={form.address1} onChange={handleChange} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address Line 2</label>
              <input type="text" name="address2" value={form.address2} onChange={handleChange} className={inputCls} />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className={labelCls}>City</label>
                <input type="text" name="city" value={form.city} onChange={handleChange} className={inputCls} />
              </div>
              <div className="flex-1">
                <label className={labelCls}>Postcode</label>
                <input type="text" name="postcode" value={form.postcode} onChange={handleChange} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Country</label>
              <input type="text" name="country" value={form.country} onChange={handleChange} className={inputCls} />
            </div>
            <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md mt-4">
              Save
            </button>
            {saved && <div className="text-green-600 dark:text-green-400 mt-2 text-sm">✓ Address details saved!</div>}
            {saveError && <div className="text-red-600 dark:text-red-400 mt-2 text-sm">⚠️ {saveError}</div>}
          </form>

          <h2 className="text-lg font-semibold mb-2 mt-6 text-gray-900 dark:text-white">Data Retention</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Controls how long sensor readings are kept. By default records are kept indefinitely — set a limit here if you need older readings purged automatically.
          </p>
          {retentionLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          <form onSubmit={handleRetentionSave} className="space-y-4 bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="retentionMode"
                  checked={retentionMode === 'indefinite'}
                  onChange={() => { setRetentionMode('indefinite'); setRetentionSaved(false); }}
                />
                Keep sensor readings indefinitely
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="retentionMode"
                  checked={retentionMode === 'limited'}
                  onChange={() => { setRetentionMode('limited'); setRetentionSaved(false); }}
                />
                Automatically delete readings older than
                <input
                  type="number"
                  min={1}
                  value={retentionMonthsInput}
                  onChange={e => { setRetentionMonthsInput(e.target.value); setRetentionSaved(false); }}
                  onFocus={() => setRetentionMode('limited')}
                  className="w-20 rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset px-2 py-1"
                />
                months
              </label>
            </div>
            <button type="submit" disabled={retentionSaving} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-4 py-2 rounded-md mt-2">
              {retentionSaving ? 'Saving…' : 'Save'}
            </button>
            {retentionSaved && <div className="text-green-600 dark:text-green-400 mt-2 text-sm">✓ Retention policy saved!</div>}
            {retentionError && <div className="text-red-600 dark:text-red-400 mt-2 text-sm">⚠️ {retentionError}</div>}
          </form>

          <h2 className="text-lg font-semibold mb-2 mt-6 text-gray-900 dark:text-white">Sensor Data Storage</h2>
          {storageLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!storageLoading && storageStatus && (
            <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {formatBytes(storageStatus.bytesUsed)} used of {formatBytes(storageStatus.capBytes)} allowance
              </div>
              {storageStatus.lastCheckedAt && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Last checked {new Date(storageStatus.lastCheckedAt).toLocaleString()}
                </div>
              )}
              {storageStatus.overCap && (
                <div className="text-amber-700 dark:text-amber-400 mt-3 text-sm">
                  ⚠️ You're over your sensor data storage allowance. Contact support to archive older readings.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Branding tab ── */}
      {activeTab === 'branding' && (
        <>
          <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white">Reseller Branding</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Customise how the system appears to your customers — your logo, colours, and contact details will appear in the dashboard, PDF reports, and email communications.
          </p>
          {loadingSettings && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          <form onSubmit={handleBrandingSave} className="space-y-6 bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">

            {/* Logo upload */}
            <div>
              <label className={labelCls}>Company Logo</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">PNG, JPG or SVG · max 2 MB · shown in the dashboard header and PDF reports</p>
              <div className="flex items-start gap-4">
                {brandingForm.logoUrl ? (
                  <div className="relative">
                    <img src={brandingForm.logoUrl} alt="Logo preview" className="h-16 max-w-[160px] object-contain border border-gray-200 dark:border-gray-600 rounded p-1 bg-app-inset" />
                    <button
                      type="button"
                      onClick={() => setBrandingFormState(prev => ({ ...prev, logoUrl: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      title="Remove logo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className="flex flex-col items-center justify-center w-40 h-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:border-indigo-400 transition-colors"
                  >
                    <Upload className="h-5 w-5 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-400">Upload logo</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {brandingForm.logoUrl ? 'Change' : 'Browse'}
                </button>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </div>
            </div>

            {/* Brand colour */}
            <div>
              <label className={labelCls}>Brand Colour</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Used as the primary accent colour throughout the interface</p>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandingForm.brandColor}
                  onChange={e => setBrandingFormState(prev => ({ ...prev, brandColor: e.target.value }))}
                  className="h-10 w-16 rounded border border-gray-300 dark:border-gray-600 cursor-pointer p-0.5 bg-app-inset"
                />
                <input
                  type="text"
                  value={brandingForm.brandColor}
                  onChange={e => setBrandingFormState(prev => ({ ...prev, brandColor: e.target.value }))}
                  className="w-32 rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset px-3 py-2"
                  placeholder="#4f46e5"
                />
              </div>
            </div>

            {/* Contact details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Email Address</label>
                <input
                  type="email"
                  value={brandingForm.emailAddress}
                  onChange={e => setBrandingFormState(prev => ({ ...prev, emailAddress: e.target.value }))}
                  placeholder="info@yourcompany.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Phone Number</label>
                <input
                  type="tel"
                  value={brandingForm.phone}
                  onChange={e => setBrandingFormState(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+44 20 1234 5678"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Website</label>
                <input
                  type="text"
                  value={brandingForm.website}
                  onChange={e => setBrandingFormState(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="www.yourcompany.com"
                  className={inputCls}
                />
              </div>
            </div>

            {/* Report & email customisation */}
            <div>
              <label className={labelCls}>Report Header Text</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Appears in the top-right of every PDF report (defaults to company name)</p>
              <input
                type="text"
                value={brandingForm.reportHeader}
                onChange={e => setBrandingFormState(prev => ({ ...prev, reportHeader: e.target.value }))}
                placeholder="e.g. ACME Monitoring Services"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email Footer Text</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Appears at the bottom of password reset and alarm notification emails</p>
              <input
                type="text"
                value={brandingForm.emailFooter}
                onChange={e => setBrandingFormState(prev => ({ ...prev, emailFooter: e.target.value }))}
                placeholder="e.g. ACME Ltd · acme.com · 020 1234 5678"
                className={inputCls}
              />
            </div>

            <button
              type="submit"
              disabled={brandingSaving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-4 py-2 rounded-md"
            >
              {brandingSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {brandingSaving ? 'Saving…' : 'Save Branding'}
            </button>
            {brandingSaved && <div className="text-green-600 dark:text-green-400 mt-2 text-sm">✓ Branding saved! Changes are live immediately.</div>}
            {brandingSaveError && <div className="text-red-600 dark:text-red-400 mt-2 text-sm">⚠️ {brandingSaveError}</div>}
          </form>
        </>
      )}

      {/* ── SMS Credits tab ── */}
      {activeTab === 'sms-credits' && (
        <>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">SMS Credits</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Count of alert messages dispatched via TextMagic. Each row is one credit consumed.
              </p>
            </div>
            <button
              onClick={loadSmsCredits}
              disabled={smsCreditsLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${smsCreditsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div
            role="note"
            className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 h-5 w-5 flex-none" />
              <div>
                <h3 className="text-sm font-semibold">Rolling 24-hour safety limits</h3>
                <p className="mt-1 text-sm">
                  Each customer is limited to a maximum of <strong>200 SMS messages</strong> and{' '}
                  <strong>1,000 emails</strong> in any rolling 24-hour period. Once a limit is reached,
                  further messages on that channel are suppressed until usage falls below the limit.
                  Alarms remain visible in the dashboard.
                </p>
                <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
                  Repeat notifications are also restricted to a minimum interval of 15 minutes.
                </p>
              </div>
            </div>
          </div>

          {smsCreditsLoading && !smsCredits && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
            </div>
          )}
          {smsCreditsError && (
            <div className="text-red-600 dark:text-red-400 text-sm mb-4">⚠️ {smsCreditsError}</div>
          )}

          {smsCredits && (
            <>
              {/* Top-level totals */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    <MessageSquare className="h-4 w-4" /> Total SMS
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{smsCredits.totalSmsSent.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">All-time messages sent via TextMagic</div>
                </div>
                <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    <MessageSquare className="h-4 w-4" /> This Month
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{smsCredits.smsThisMonth.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Last month: {smsCredits.smsLastMonth.toLocaleString()}
                  </div>
                </div>
                <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    <Mail className="h-4 w-4" /> Total Emails
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{smsCredits.totalEmailSent.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This month: {smsCredits.emailThisMonth.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Monthly breakdown */}
              <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Monthly breakdown (last 12 months)</h3>
                {smsCredits.byMonth.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No alert notifications have been dispatched yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 pr-4 font-medium">Month</th>
                          <th className="py-2 pr-4 font-medium text-right">SMS</th>
                          <th className="py-2 font-medium text-right">Email</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {smsCredits.byMonth.map(row => (
                          <tr key={row.month}>
                            <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{formatMonthLabel(row.month)}</td>
                            <td className="py-2 pr-4 text-right font-medium text-gray-900 dark:text-gray-100">{row.sms.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-700 dark:text-gray-300">{row.email.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Counts come from the alert dispatch log — every row where an SMS was actually sent counts as one TextMagic credit.
              </p>
            </>
          )}
        </>
      )}

      {/* ── Appearance tab ── */}
      {activeTab === 'appearance' && (
        <div className="bg-app-panel dark:bg-gray-800 rounded-lg shadow border-2 border-indigo-500 dark:border-indigo-400 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Choose between light and dark mode</p>
            </div>
            <button
              onClick={handleThemeToggle}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {isDark ? (
                <><Sun className="h-5 w-5 text-yellow-500" /><span className="text-sm">Light Mode</span></>
              ) : (
                <><Moon className="h-5 w-5 text-gray-600" /><span className="text-sm">Dark Mode</span></>
              )}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SettingsPage;
